import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm transition-colors focus-visible:outline-none focus:ring-0 disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-card text-primary gap-2 border border-primary rounded-[4px] shadow-sm hover:bg-[#F8F7FF] dark:bg-[#18181C] dark:text-primary dark:border-primary dark:hover:bg-[#23232A] font-medium',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 dark:bg-red-800 dark:text-white',
        outline:
          'border border-gray-300 bg-card shadow-sm hover:bg-[#F8F7FF] dark:border-gray-700 dark:bg-[#18181C] dark:hover:bg-[#23232A] font-medium',
        coloredOutline:
          'bg-card text-primary gap-2 border border-primary rounded-[4px] shadow-sm hover:bg-[#F8F7FF] dark:bg-[#18181C] dark:text-primary dark:border-primary dark:hover:bg-[#23232A] font-medium',
        coloredOutlineRed:
          'bg-card text-red-500 gap-2 border border-red-500 rounded-[4px] shadow-sm hover:bg-[#F8F7FF] dark:bg-[#18181C] dark:text-red-400 dark:border-red-400 dark:hover:bg-[#23232A] font-medium',
        secondary:
          'bg-[#FAFAFA] dark:bg-[#18181C] gap-2 border border-gray-300 dark:border-gray-700 shadow-sm rounded-[4px] hover:bg-[#EBEBEB] dark:hover:bg-[#23232A] font-medium',
        ghost:
          'text-accent-foreground hover:bg-inherit hover:text-accent-foreground focus-visible:ring-0 dark:text-accent-foreground',
        link: 'text-primary dark:text-white underline-offset-4 hover:underline',
        color:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground',
        grayBorderVariant:
          'bg-card  gap-2 border border-gray-300 rounded-[4px] shadow-sm hover:bg-[#F8F7FF] dark:bg-[#18181C] dark:text-primary dark:border-gray-600 dark:hover:bg-[#23232A] font-medium'
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-sm px-3 text-xs',
        lg: 'h-10 rounded-sm px-8',
        icon: 'h-9 w-9'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
